import {
  EscposBuilder,
  headDotsForPaper,
  justify,
  textBoxForPaper,
  layoutRow,
  ruleLine,
  wrap,
  type Align,
  type RowCell,
} from './bytes'
import { imageToRaster } from './raster'

/**
 * Receipt HTML → ESC/POS bytes (F-FE-N105).
 *
 * ── Why render from the HTML instead of building a second document model ──
 *
 * `printCustomerReceipt.ts` is ~2,400 lines, and the layout is inseparable from
 * the business logic: which tender's tax rate to show on a provisional slip,
 * whether deductions print above the charges derived from them, how a card promo
 * relabels the discount row. Eight documents share that code — receipt, refund,
 * unpaid bill, split guest, Z-report, shift report, credit note, PRA invoice.
 *
 * Re-deriving all of that in a parallel ESC/POS builder would duplicate the
 * subtlest money logic in the app, and the two copies would drift. This codebase
 * already knows what that costs — it is why the line-modifier arithmetic is
 * pinned three ways against shared fixtures.
 *
 * So the HTML stays the single source of truth and this is a second RENDERER of
 * it, exactly as the desktop shell is (HTML → printToPDF → SumatraPDF). Every
 * document works the day it is written, and a change to a receipt reaches the
 * tablet with no second edit.
 *
 * ── Why computed style and not class names ──
 *
 * The receipt CSS carries 40+ class names. Matching them would be a second
 * source of truth by another route, and would break silently the first time one
 * was renamed. Instead the HTML is laid out in a real (off-screen) iframe and
 * every decision is read from `getComputedStyle`: alignment, weight, size,
 * whether a row is a two-column flex pair. That survives a restyle.
 *
 * ── What the first tablet receipts got wrong (F-FE-N107) ──
 *
 * Mizaj printed the first real ones and they came back with no dividers at all
 * and the line items in a ragged stack. Two causes, both in this file:
 *
 *  1. A separator was only recognised on an EMPTY element. Every divider on the
 *     receipt is a `border-top` / `border-bottom` on a block that also carries
 *     content — the section headings, the box around the items header, the rule
 *     above the grand total. So every one of them was dropped.
 *
 *  2. The line items are a real `<table>`, and `<tr>` / `<td>` carry the display
 *     values `table-row` / `table-cell`, which nothing here handled. The walker
 *     fell through to the text nodes and printed one cell per line.
 *
 * Both are now handled structurally, so they hold for the refund slip, the
 * Z-report's tender table and the PRA invoice too — none of which had ever been
 * printed when this was written.
 */

export interface HtmlToEscposOptions {
  paperWidthMm: number
  /** Head width in dots — 576 for 80mm, 384 for 58mm. */
  dotsPerLine?: number
  /**
   * Extra character cells to inset from the left (F-FE-N113).
   *
   * Sent as literal spaces, not as a bigger `GS L`: from a photograph there is
   * no way to tell whether a slip sits left because the printer ignored our
   * margin command or because the roll is seated off centre, and a space is a
   * character no firmware can decline to print. Machine-local — see
   * getPrintOffsetCols in stationPrinterMap.ts.
   */
  offsetCols?: number
}

/** Ink the whole document, or null when the DOM is unavailable. */
export async function renderHtmlToEscpos(
  html: string,
  opts: HtmlToEscposOptions,
): Promise<Uint8Array | null> {
  if (typeof document === 'undefined') return null

  // F-FE-N110: leave a character of margin each side rather than printing into
  // the outermost cell of the head. See textBoxForPaper.
  const box = textBoxForPaper(opts.paperWidthMm)
  // The offset eats into the line, it does not widen the slip.
  const offset = Math.max(0, Math.min(opts.offsetCols ?? 0, box.columns - 8))
  const columns = box.columns - offset
  const pad = ' '.repeat(offset)
  const dots = opts.dotsPerLine ?? headDotsForPaper(opts.paperWidthMm)

  // Off-screen but LAID OUT: computed styles and flex detection need real
  // layout, so `display:none` would give us nothing to read.
  const frame = document.createElement('iframe')
  frame.setAttribute(
    'style',
    'position:fixed;left:-10000px;top:0;width:420px;height:2000px;border:0;opacity:0;pointer-events:none',
  )
  document.body.appendChild(frame)

  try {
    const doc = frame.contentDocument
    if (!doc) return null
    doc.open()
    doc.write(html)
    doc.close()

    const body = doc.body
    if (!body) return null

    // Wait for the images before reading any geometry (F-FE-N110).
    //
    // An <img> that has not decoded yet has no intrinsic ratio, so `height:auto`
    // resolves to 0 and `max-height: 22mm` has nothing to cap. Measuring then
    // gave the logo a width and no height, and it rastered square at the full
    // width of the box — 50mm of artwork above a 40mm receipt. Everything below
    // reads getBoundingClientRect(), so the wait belongs here, once, rather than
    // at each measurement.
    await settleImages(doc)

    const b = new EscposBuilder().init().printArea(box.widthDots, box.leftDots)
    const ctx: WalkCtx = {
      columns,
      dots,
      doc,
      out: b,
      lastWasBlank: true,
      lastRule: null,
      baseFontPx: baseFontSize(doc, body),
      dotsPerPx: dotsPerPx(doc, body, box.widthDots),
      pad,
    }
    await walk(body, ctx)
    b.align('left').bold(false).size(1, 1).cut()
    return b.build()
  } finally {
    try {
      document.body.removeChild(frame)
    } catch {
      /* already gone */
    }
  }
}

interface WalkCtx {
  columns: number
  dots: number
  doc: Document
  out: EscposBuilder
  lastWasBlank: boolean
  /** The rule most recently drawn, so collapsed CSS borders don't double up. */
  lastRule: string | null
  /**
   * The receipt's own body size in px.
   *
   * Emphasis is decided RELATIVE to this, never against a fixed pixel number.
   * Every size on the slip is `calc(BASE * var(--rc-scale))`, and the scale
   * carries both a 0.9 baseline and the tenant's `receipt_font_scale` — at
   * Mizaj's 0.95 the business name computes to 18.8px. Against the old fixed
   * `>= 20px` test nothing on the receipt was ever emphasised, so the whole slip
   * printed flat with no hierarchy at all.
   */
  baseFontPx: number
  /**
   * Printer dots per CSS pixel in the measuring iframe.
   *
   * Derived, not guessed. Images used to be scaled by a hardcoded `x3`, which on
   * a 72mm receipt laid out at ~272px overshot by half and pinned every logo to
   * the full head width. The receipt's own rendered width maps to the text box,
   * so that ratio is the only honest conversion.
   */
  dotsPerPx: number
  /** Leading spaces prefixed to every left-laid line (F-FE-N113). */
  pad: string
}

const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'HEAD', 'META', 'LINK', 'TITLE'])

/**
 * Resolve once every image has loaded, failed, or the budget runs out.
 *
 * Budgeted because a receipt logo can be a remote URL on a till whose network
 * is down, and a cashier waiting on a print must not wait on that. A slip whose
 * logo is sized from a stale box is worse than one printed a beat later, but
 * both beat one that never comes out.
 */
function settleImages(doc: Document, budgetMs = 2000): Promise<void> {
  const imgs = Array.from(doc.images ?? [])
  const pending = imgs.filter((im) => !im.complete)
  if (pending.length === 0) return Promise.resolve()
  return new Promise((resolve) => {
    let left = pending.length
    let done = false
    const finish = () => {
      if (done) return
      done = true
      resolve()
    }
    const tick = () => {
      left--
      if (left <= 0) finish()
    }
    for (const im of pending) {
      im.addEventListener('load', tick, { once: true })
      im.addEventListener('error', tick, { once: true })
    }
    setTimeout(finish, budgetMs)
  })
}

function baseFontSize(doc: Document, body: HTMLElement): number {
  const el = (doc.querySelector('.receipt') as HTMLElement | null) ?? body
  const px = parseFloat(doc.defaultView?.getComputedStyle(el)?.fontSize ?? '13')
  return px > 0 ? px : 13
}

function dotsPerPx(doc: Document, body: HTMLElement, widthDots: number): number {
  const el = (doc.querySelector('.receipt') as HTMLElement | null) ?? body
  const px = el.getBoundingClientRect().width
  // 2.1 is what a 72mm receipt at 96dpi works out to; only used if the slip has
  // no measurable width, which means no images will be measured either.
  return px > 0 ? widthDots / px : 2.1
}

async function walk(el: Element, ctx: WalkCtx): Promise<void> {
  for (const node of Array.from(el.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      // Text directly under a container that also has element children is
      // usually incidental whitespace; emitText ignores empties anyway.
      emitText(node.textContent ?? '', el as HTMLElement, ctx)
      continue
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue
    const child = node as HTMLElement
    if (SKIP_TAGS.has(child.tagName)) continue

    const style = styleOf(child, ctx)
    if (style?.display === 'none' || style?.visibility === 'hidden') continue

    if (child.tagName === 'IMG') {
      await emitImage(child as HTMLImageElement, ctx)
      continue
    }

    if (child.tagName === 'HR') {
      rule(ctx, '-'.repeat(ctx.columns))
      continue
    }

    if (child.tagName === 'TR') {
      emitRow(child, ctx)
      continue
    }

    // A CSS border is how the receipt draws every one of its separators, and it
    // sits on the element it belongs to rather than on an empty spacer: above
    // the section heading it introduces, below the items header it closes.
    rule(ctx, borderRule(style, 'Top', ctx))
    await emitElement(child, style, ctx)
    rule(ctx, borderRule(style, 'Bottom', ctx))
  }
}

async function emitElement(
  child: HTMLElement,
  style: CSSStyleDeclaration | undefined,
  ctx: WalkCtx,
): Promise<void> {
  // The label/value pair — the receipt's most common row.
  const pair = twoColumnPair(child)
  if (pair) {
    emitLine(justify(pair.left, pair.right, effectiveColumns(style, ctx)), style, ctx, true)
    return
  }

  if (isBlock(style)) {
    if (child.children.length === 0) {
      const text = (child.textContent ?? '').replace(/\s+/g, ' ').trim()
      if (text) emitLine(text, style, ctx)
      else blank(ctx)
      return
    }
  }
  await walk(child, ctx)
}

function styleOf(el: HTMLElement, ctx: WalkCtx): CSSStyleDeclaration | undefined {
  return ctx.doc.defaultView?.getComputedStyle(el)
}

/** The rule a block's top or bottom border becomes, or '' for no border. */
function borderRule(
  style: CSSStyleDeclaration | undefined,
  side: 'Top' | 'Bottom',
  ctx: WalkCtx,
): string {
  if (!style) return ''
  const edge = side.toLowerCase()
  const width = parseFloat(style.getPropertyValue(`border-${edge}-width`) || '0')
  const kind = style.getPropertyValue(`border-${edge}-style`) || 'none'
  return ruleLine(kind, isNaN(width) ? 0 : width, ctx.columns)
}

/**
 * One `<tr>` as aligned columns.
 *
 * Column widths come from what the browser actually laid out, so `colspan`, the
 * CSS percentages and `table-layout: fixed` are all respected without this
 * knowing any of them exist. The header row's border is drawn once for the row
 * rather than once per cell — `border-collapse: collapse` means five `<th>`
 * borders are one line on screen, and they must be one line on paper too.
 */
function emitRow(tr: HTMLElement, ctx: WalkCtx): void {
  const cellEls = (Array.from(tr.children) as HTMLElement[]).filter(
    (c) => c.tagName === 'TD' || c.tagName === 'TH',
  )
  if (cellEls.length === 0) return

  const first = styleOf(cellEls[0], ctx)
  rule(ctx, borderRule(first, 'Top', ctx))

  const rowWidth =
    tr.getBoundingClientRect().width ||
    cellEls.reduce((s, c) => s + c.getBoundingClientRect().width, 0)

  const cells: RowCell[] = cellEls.map((c) => {
    const s = styleOf(c, ctx)
    const w = c.getBoundingClientRect().width
    return {
      text: (c.textContent ?? '').replace(/\s+/g, ' ').trim(),
      align: alignOf(s),
      fraction: rowWidth > 0 && w > 0 ? w / rowWidth : 1 / cellEls.length,
      // The money columns are `white-space: nowrap` in the receipt CSS, which
      // says exactly what layoutRow needs to know: this figure may not be
      // broken across two lines. Read from the style, so a new numeric column
      // inherits the protection by being styled like its neighbours.
      noWrap: (s?.whiteSpace ?? '').startsWith('nowrap'),
    }
  })

  const bold = isBold(first)
  const lines = layoutRow(cells, ctx.columns)
  if (lines.length > 0) {
    ctx.out.align('left')
    if (bold) ctx.out.bold(true)
    for (const ln of lines) ctx.out.line(ctx.pad + ln)
    if (bold) ctx.out.bold(false)
    ctx.lastWasBlank = false
    ctx.lastRule = null
  }

  rule(ctx, borderRule(first, 'Bottom', ctx))
}

/** Double-width text halves the usable columns; laying out for the full width shears every row under it. */
function effectiveColumns(style: CSSStyleDeclaration | undefined, ctx: WalkCtx): number {
  return isLarge(style, ctx) ? Math.floor(ctx.columns / 2) : ctx.columns
}

function isBlock(style: CSSStyleDeclaration | undefined): boolean {
  const d = style?.display ?? 'block'
  return d === 'block' || d === 'flex' || d === 'grid' || d === 'list-item' || d === 'table'
}

/** Emphasised enough to print double-size — measured against the slip's own body text. */
function isLarge(style: CSSStyleDeclaration | undefined, ctx: WalkCtx): boolean {
  const px = parseFloat(style?.fontSize ?? '')
  if (isNaN(px)) return false
  return px >= ctx.baseFontPx * 1.5
}

function isBold(style: CSSStyleDeclaration | undefined): boolean {
  const w = style?.fontWeight ?? '400'
  const n = parseInt(w, 10)
  return w === 'bold' || w === 'bolder' || (!isNaN(n) && n >= 600)
}

function alignOf(style: CSSStyleDeclaration | undefined): Align {
  const a = style?.textAlign ?? 'left'
  if (a === 'center') return 'center'
  if (a === 'right' || a === 'end') return 'right'
  // A flex row with space-between is a justified pair, handled elsewhere; here
  // it just means "not centred".
  return 'left'
}

/**
 * A block whose children lay out as side-by-side runs of text.
 *
 * Two cells is the totals row — "Subtotal / 1,250.00". THREE is the item row —
 * "Latte / 2 / 1,700.00" — and getting that wrong is not cosmetic: falling
 * through to the generic block path would print every item as three separate
 * lines, which is a receipt nobody can read.
 *
 * Everything but the last cell becomes the label, the last becomes the value,
 * so 2 and 3+ are handled by one rule. Detected structurally rather than by
 * class name, so a restyle cannot silently break it.
 */
function twoColumnPair(el: HTMLElement): { left: string; right: string } | null {
  const kids = Array.from(el.children) as HTMLElement[]
  if (kids.length < 2 || kids.length > 4) return null
  // The only question that matters is whether the browser put these children on
  // ONE line, and the browser has already answered it.
  if (!laidOutSideBySide(kids)) return null
  if (kids.some((k) => k.querySelector('img'))) return null
  // Any cell that is itself a multi-line block is not a row cell — walking it
  // properly beats flattening it onto one line.
  if (kids.some((k) => k.children.length > 1)) return null

  const cells = kids.map((k) => (k.textContent ?? '').replace(/\s+/g, ' ').trim())
  const right = cells[cells.length - 1]
  const left = cells.slice(0, -1).filter(Boolean).join('  ')
  if (!left && !right) return null
  return { left, right }
}

/**
 * Did the browser actually lay these children out on one line? (F-FE-N107)
 *
 * This used to be inferred from `display`, and `block` was on the allowed list
 * — so ANY container holding two to four stacked `<div>`s was mistaken for a
 * row. The receipt's contact block is exactly that, and Mizaj's slip came out
 * with the phone number shoved hard left and the website hard right on one
 * line, instead of two centred lines.
 *
 * Geometry cannot be fooled the way a display value can: cells of one row
 * overlap vertically and run left to right. Stacked blocks do neither.
 */
function laidOutSideBySide(kids: HTMLElement[]): boolean {
  const rects = kids.map((k) => k.getBoundingClientRect())
  if (rects.some((r) => r.width === 0 && r.height === 0)) return false
  for (let i = 1; i < rects.length; i++) {
    // Below its predecessor rather than beside it: a stack, not a row.
    if (rects[i].top >= rects[i - 1].bottom - 0.5) return false
    // Not strictly left-to-right: overlapping or reordered, so column
    // arithmetic would be meaningless.
    if (rects[i].left < rects[i - 1].right - 0.5) return false
  }
  return true
}

function emitText(raw: string, parent: HTMLElement, ctx: WalkCtx): void {
  const t = raw.replace(/\s+/g, ' ').trim()
  if (!t) return
  emitLine(t, styleOf(parent, ctx), ctx)
}

function emitLine(
  text: string,
  style: CSSStyleDeclaration | undefined,
  ctx: WalkCtx,
  /**
   * Already laid out to the column width by `justify` — must be written as-is.
   * Wrapping it would collapse the padding and destroy the alignment of every
   * money column on the receipt. Passed explicitly rather than sniffed for
   * double spaces: a row whose label nearly fills the line has a single space
   * before its value and would have been silently re-flowed.
   */
  preformatted = false,
): void {
  const t = text.trim()
  if (!t) {
    blank(ctx)
    return
  }
  const large = isLarge(style, ctx)
  const cols = effectiveColumns(style, ctx)
  const align = alignOf(style)

  ctx.out.align(align)
  if (isBold(style) || large) ctx.out.bold(true)
  if (large) ctx.out.size(2, 2)

  // Centred lines are positioned by the printer (ESC a 1), so a leading space
  // would do nothing there; everything laid out left carries the inset.
  const lines = preformatted ? [t] : wrap(t, cols)
  for (const ln of lines) ctx.out.line(align === 'center' ? ln : ctx.pad + ln)

  if (large) ctx.out.size(1, 1)
  if (isBold(style) || large) ctx.out.bold(false)
  ctx.out.align('left')
  ctx.lastWasBlank = false
  ctx.lastRule = null
}

/**
 * Draw a divider, unless it would repeat the one just drawn.
 *
 * `border-collapse: collapse` merges adjacent borders into one line on screen,
 * and the receipt relies on that: the rule closing the items header is also the
 * rule opening the first item row. Printing both would double every divider.
 */
function rule(ctx: WalkCtx, line: string): void {
  if (!line) return
  if (ctx.lastRule === line) return
  ctx.out.align('left').line(ctx.pad + line)
  ctx.lastRule = line
  // A rule already separates what is above from what is below, so a blank line
  // immediately after it is wasted paper.
  ctx.lastWasBlank = true
}

/** Collapse runs of empty blocks — HTML is full of them and paper is not free. */
function blank(ctx: WalkCtx): void {
  if (ctx.lastWasBlank) return
  ctx.out.line('')
  ctx.lastWasBlank = true
}

async function emitImage(img: HTMLImageElement, ctx: WalkCtx): Promise<void> {
  const src = img.getAttribute('src') ?? ''
  if (!src) return
  // Respect the rendered BOX — both axes (F-FE-N110). The FBR mark is a 240px
  // asset printed at 44px, a QR is sized in mm for scannability, and the logo
  // carries `max-height: 22mm`. Reading width alone is what printed Mizaj a
  // 72mm-tall logo above a 40mm receipt.
  const shown = img.getBoundingClientRect()
  const wantW = Math.max(
    64,
    Math.min(ctx.dots, Math.round((shown.width || img.width || 120) * ctx.dotsPerPx)),
  )
  const wantH = shown.height > 0 ? Math.round(shown.height * ctx.dotsPerPx) : 0
  const raster = await imageToRaster(src, {
    maxWidthDots: wantW,
    ...(wantH > 0 ? { maxHeightDots: wantH } : {}),
  })
  if (raster.length === 0) return
  ctx.out.align('center').bytes(raster).align('left')
  ctx.lastWasBlank = false
  ctx.lastRule = null
}
